/**
 * companyMenuSet.js — 선택된 회사의 관리 메뉴 세트 빌더 (2026-09-03 회사 선택창 개편 1차)
 *
 * 항목 정의(라벨·아이콘·주소·노출조건)는 **companyFeatures.js 한 장**에 있다 (2026-09-11 C2).
 * 여기서는 그 표를 읽어 링크로 만들기만 한다. 같은 표를 AppRoutes 가 라우트로도 만들기 때문에
 * "메뉴에는 뜨는데 주소가 없다" 가 생길 자리가 없다.
 *
 * 라벨에 "(회사명)" 접미사를 붙이지 않는다 — 회사 맥락은 상단 선택창이 제공한다.
 */
import { COMPANY_MENU_FEATURES, companyFeaturePath } from './companyFeatures';
import { filterMenuGroups } from './sidebarFilters';

// 회사 선택과 무관하게 항상 보이는 그룹 (홈·상황판 바로가기는 SidebarContent 상단 고정이라 별도)
export const GLOBAL_GROUP_NAMES = new Set(['시스템']);

/**
 * 외부(추가) 회사 1개의 접근 가능한 관리 메뉴 항목 목록.
 * mode 'ext' = 타사(항목 전부) / 'own' = 자기 회사(일반 메뉴로 전환된 도메인은 dupGuard 로 중복 숨김)
 * keepOwnCall (2026-09-24): 일일콜 켠 회사가 2곳 이상인 고객 테넌트의 메인 회사 관리자.
 *   일반 메뉴 '일일콜현황 관리'가 (통합)이 되므로, 자기 회사 콜만 보는 회사 메뉴를 숨기지 않고 함께 둔다.
 *   그 회사 콜 권한이 **명시적으로 있을 때만**(af.call === true) — 권한 없으면 숨김(fail-closed).
 */
export function buildCompanyItems(company, { permCodes, mode = 'ext', keepOwnCall = false } = {}) {
  const code = company.code; // URL 은 원본 케이스 유지 (기존 아코디언과 동일)
  const af = company.accessible_features ?? null;
  const ef = company.enabled_features || {};
  // 자기 회사(메인)일 때: 이미 일반 메뉴로 전환된 도메인은 숨겨 중복 방지 (구 아코디언 dupGuard)
  //   generalCovers(permCodes, af) 가 있는 기능은 그 판단을 따른다 — 일반 메뉴가 이 회사 화면의 탭을 **다** 줄 때만 숨긴다
  //   (출퇴근 관리 2026-09-29: 일반 달력 열쇠만 있으면 일반 화면은 달력 탭뿐이라, 회사 상세 열쇠가 있으면 회사 메뉴를 남긴다).
  const dupGuard = (f) => {
    if (mode !== 'own' || !permCodes) return true;
    if (typeof f.generalCovers === 'function') return !f.generalCovers(permCodes, af);
    return !(f.generalCode && permCodes.has(f.generalCode));
  };
  const keepCall = (f) => f.key === 'call' && keepOwnCall && Boolean(af) && af.call === true;

  return COMPANY_MENU_FEATURES
    .filter((f) => f.show(ef, af) && (keepCall(f) || dupGuard(f)))
    .map((f) => ({
      group: f.group,
      hqPath: f.hqPath || null, // 같은 기능의 일반 메뉴가 놓인 분류를 따라가려고(menuComposition)
      to: companyFeaturePath(code, f),
      label: f.label,
      Icon: f.Icon,
      desc: f.desc,
    }));
}

// ── 운영자가 고객 테넌트를 골랐을 때 (2026-09-15) ────────────────────────────
// 사용자 신고: link 로 작업대상=유기자차를 고르면 부여된 미팅지도 관리는 안 보이고,
//   부여 안 된 급여 관리 등은 보였다. 옛 enabled_features(없으면 켜짐) + 회사 공통 16종만 그렸기 때문.
// 이제 **그 테넌트 관리자와 같은 메뉴**를 그린다. 권한은 서버 menu-perms(= 테넌트 관리자 권한 계산)에서 온다.

// 회사별 주소가 없는 일반 화면 중, 운영자 선택값(X-Scope-Company)으로 서버가 **그 회사 데이터만** 주는 것이
//   화면 전체 API 대조로 확인된 화면. 여기 없는 일반 화면은 열면 전 테넌트 데이터가 섞이므로 숨긴다(fail-closed).
export const SCOPE_READY_PATHS = new Set([
  '/admin/meeting-map', '/admin/contracts', '/admin/products',
  // 2026-09-15 추가: 조회 API 12종 대조 동일(6종은 실제로 좁혀짐), 생성은 scopedCompanyCode 로 저장
  '/admin/quotes', '/admin/reservations', '/admin/reservation-revenue',
]);

/** 고객 테넌트(대표 회사) 메뉴 그룹. permCodes 가 없으면 빈 배열. 시스템 그룹은 호출부가 운영자 것을 붙인다. */
export function buildCustomerTenantGroups(companyCode, permCodes, menuGroups) {
  if (!(permCodes instanceof Set)) return [];
  return filterMenuGroups(menuGroups, permCodes, false)
    .filter((g) => !GLOBAL_GROUP_NAMES.has(g.group))
    .map((g) => ({
      ...g,
      items: g.items
        .map((it) => {
          // 회사별 주소가 있으면 그 주소(주소 자체가 회사를 정한다)
          const f = COMPANY_MENU_FEATURES.find((x) => x.hqPath && x.hqPath === it.to);
          if (f) return { ...it, to: companyFeaturePath(companyCode, f) };
          return SCOPE_READY_PATHS.has(it.to) ? it : null;
        })
        .filter(Boolean),
    }))
    .filter((g) => g.items.length > 0);
}

/** 항목들을 그룹 배열로 묶는다. groupOrder(MENU_GROUPS 의 그룹명 순서)를 따른다. */
export function groupCompanyItems(items, groupOrder) {
  const byGroup = new Map();
  for (const item of items) {
    if (!byGroup.has(item.group)) byGroup.set(item.group, []);
    byGroup.get(item.group).push(item);
  }
  const ordered = [];
  for (const name of groupOrder) {
    if (byGroup.has(name)) {
      ordered.push({ group: name, items: byGroup.get(name) });
      byGroup.delete(name);
    }
  }
  for (const [name, groupItems] of byGroup) ordered.push({ group: name, items: groupItems });
  return ordered;
}
